import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  History,
  Users,
  CheckCircle2,
  XCircle,
  Star,
  TrendingUp,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target,
  ArrowLeft,
  MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * SimulationHistory - View past simulation feedback and scores
 * Displays all completed simulations grouped by topic
 */
const SimulationHistory = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState({});

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rpat/phase4-simulations/history');
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load simulation history');
    } finally {
      setLoading(false);
    }
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6 max-w-4xl mx-auto">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/rpat')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to R-PAT
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <History className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Simulation History</h1>
            <p className="text-muted-foreground">Review your past simulation attempts and AI feedback</p>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-white">{history?.total_sessions || 0}</div>
              <p className="text-sm text-slate-400">Total Attempts</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-emerald-400">
                {history?.topics?.reduce((acc, t) => 
                  acc + t.sessions.filter(s => s.passed).length, 0) || 0}
              </div>
              <p className="text-sm text-slate-400">Passed</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-violet-400">{history?.topics?.length || 0}</div>
              <p className="text-sm text-slate-400">Topics Practiced</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-400">
                {history?.topics?.reduce((acc, t) => {
                  const scores = t.sessions.map(s => s.score).filter(s => s !== null);
                  return scores.length > 0 
                    ? acc + scores.reduce((a, b) => a + b, 0) / scores.length 
                    : acc;
                }, 0) / (history?.topics?.length || 1) || 0}%
              </div>
              <p className="text-sm text-slate-400">Avg Score</p>
            </CardContent>
          </Card>
        </div>

        {/* History by Topic */}
        {history?.topics?.length === 0 ? (
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Simulation History</h3>
              <p className="text-slate-400 mb-4">
                Complete your first recruiter simulation to see your history here.
              </p>
              <Button 
                onClick={() => navigate('/rpat')}
                className="bg-gradient-to-r from-violet-600 to-purple-600"
              >
                Go to R-PAT Training
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {history?.topics?.map((topic) => (
              <Card key={topic.topic_id} className="bg-slate-900 border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant="outline" className="text-violet-400 border-violet-500 mb-2">
                        {topic.topic_code}
                      </Badge>
                      <CardTitle className="text-lg">{topic.topic_title}</CardTitle>
                    </div>
                    <Badge className={
                      topic.sessions.some(s => s.passed) 
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                        : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                    }>
                      {topic.sessions.filter(s => s.passed).length}/{topic.sessions.length} Passed
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topic.sessions.map((session) => (
                      <div 
                        key={session.session_id}
                        className={`
                          rounded-lg border transition-all
                          ${session.passed 
                            ? 'bg-emerald-500/5 border-emerald-500/20' 
                            : 'bg-slate-800/50 border-slate-700'
                          }
                        `}
                      >
                        {/* Session Header */}
                        <button
                          onClick={() => toggleSession(session.session_id)}
                          className="w-full p-4 flex items-center justify-between text-left"
                          data-testid={`toggle-session-${session.session_id}`}
                        >
                          <div className="flex items-center gap-3">
                            {session.passed ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-500" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white">{session.title}</span>
                                <Badge 
                                  variant="outline" 
                                  className={
                                    session.difficulty === 'easy' 
                                      ? 'text-green-400 border-green-500' 
                                      : 'text-orange-400 border-orange-500'
                                  }
                                >
                                  {session.difficulty}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                                <span className="flex items-center gap-1">
                                  <Target className="h-3 w-3" />
                                  Score: {session.score || 0}%
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(session.completed_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {session.feedback && (
                              <Badge variant="outline" className="text-violet-400 border-violet-500">
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Feedback
                              </Badge>
                            )}
                            {expandedSessions[session.session_id] ? (
                              <ChevronUp className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                        </button>

                        {/* Expanded Feedback */}
                        {expandedSessions[session.session_id] && session.feedback && (
                          <div className="px-4 pb-4 border-t border-slate-700/50 pt-4">
                            {/* Summary */}
                            {session.feedback.summary && (
                              <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
                                <p className="text-sm text-slate-200">{session.feedback.summary}</p>
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* Strengths */}
                              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Star className="h-4 w-4 text-emerald-400" />
                                  <span className="text-sm font-medium text-emerald-400">Strengths</span>
                                </div>
                                <ul className="space-y-1">
                                  {(session.feedback.strengths || []).map((item, idx) => (
                                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                                      <span className="text-emerald-500">•</span>
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Improvements */}
                              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <TrendingUp className="h-4 w-4 text-amber-400" />
                                  <span className="text-sm font-medium text-amber-400">To Improve</span>
                                </div>
                                <ul className="space-y-1">
                                  {(session.feedback.improvements || []).map((item, idx) => (
                                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                                      <span className="text-amber-500">•</span>
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* Tips */}
                              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Lightbulb className="h-4 w-4 text-blue-400" />
                                  <span className="text-sm font-medium text-blue-400">Tips</span>
                                </div>
                                <ul className="space-y-1">
                                  {(session.feedback.tips || []).map((item, idx) => (
                                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                                      <span className="text-blue-500">•</span>
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* No Feedback Message */}
                        {expandedSessions[session.session_id] && !session.feedback && (
                          <div className="px-4 pb-4 border-t border-slate-700/50 pt-4">
                            <p className="text-sm text-slate-400 text-center">
                              No AI feedback available for this session.
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SimulationHistory;
